import moment from "moment";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { exportFile } from "components/export/common";
import { CustomToastCloseButton } from "components/icons/CustomToastCloseButton";
import ExportIcon from "components/icons/ExportIcon";
import { SuccessIcon } from "components/icons/SuccessIcon";
import ApplicationLayout from "components/layout/ApplicationLayout";
import layoutStyle from "components/layout/layout.scss";
import { createLicenseTypes } from "components/licenses/common";
import LicenseEventsTable from "components/licenses/license-history/LicenseEventsTable";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { LICENSES_ROUTE } from "components/router/Routes";
import { EVENT_ACTIONS, EVENTS, FilterState, LicenseEventQuery, SOURCE_EVENTS, TableState } from "domain/licenseEvent";
import { licenseEventsService } from "services/licenses/LicenseEventService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import buttons from "styles/buttons.scss";
import { formatDate } from "utils/format";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

const CSV_LIMIT = 100_000;
const LARGE_EXPORT_THRESHOLD = 10_000;

export default function LicenseEventsView(): JSX.Element {
    const { t } = useTranslation();
    const theme = useTheme();
    const productIdList = createLicenseTypes(true).map((each) => each.productId);
    const [state, setTableState] = React.useState<TableState>({
        licenseEvents: [],
        scrollPosition: 0,
    });
    const [filterState, setFilterState] = React.useState<FilterState>({
        quickSearchFilter: "",
        eventTypes: EVENTS,
        productIds: productIdList,
        actions: EVENT_ACTIONS,
        eventSources: Object.values(SOURCE_EVENTS),
        startDate: formatDate(moment().add(-30, "days").startOf("day")),
        endDate: formatDate(moment().endOf("day")),
    });
    const { current: abortControllers } = React.useRef<AbortController[]>([]);

    const [exportFailed, setExportFailed] = React.useState<boolean>(false);
    const [filtersMissing, setFiltersMissing] = React.useState(false);
    const [refreshCount, setRefreshCount] = React.useState(0);
    const [isExporting, setIsExporting] = React.useState(false);
    const [exportLimitExceeded, setExportLimitExceeded] = React.useState(false);
    const [isLargeExport, setIsLargeExport] = React.useState(false);
    const [startExport, setStartExport] = React.useState(false);
    const [eventCount, setEventCount] = React.useState<number>(0);

    const formSearchQuery = () => {
        const searchQuery: LicenseEventQuery = {
            search: filterState.quickSearchFilter,
            actions: filterState.eventTypes,
            productIds: filterState.productIds,
            eventActions: filterState.actions,
            eventSources: filterState.eventSources,
            from: filterState.startDate,
            to: filterState.endDate,
            cursor: state.cursor,
        };
        const abortController = new AbortController();
        abortControllers.push(abortController);
        exportLicenseEvents(searchQuery, abortController);
    };

    React.useEffect(() => {
        if (startExport) {
            formSearchQuery();
            setStartExport(false);
        }
    }, [startExport]);

    const exportLicenseEvents = (searchQuery: LicenseEventQuery, abortController: AbortController) => {
        setIsExporting(true);
        const countController = new AbortController();
        abortControllers.push(countController);
        if (eventCount > CSV_LIMIT && !startExport) {
            setExportLimitExceeded(true);
            setIsExporting(false);
            return false;
        } else if (eventCount > LARGE_EXPORT_THRESHOLD && !startExport) {
            setIsLargeExport(true);
            setIsExporting(false);
            return false;
        }
        licenseEventsService
            .exportLicenseEvents(searchQuery, abortController)
            .then((result) => {
                setIsExporting(false);
                if (result.type === "queued") {
                    return;
                }
                const url = URL.createObjectURL(result.blob);
                exportFile(url, "license_history.zip");
                toast(
                    <SuccessIcon
                        successClass={layoutStyle.customToastSuccessIcon}
                        color={theme.contentBackgroundColor}
                        text={t("LicenseEvent.export.exportCount", {
                            actualCount: eventCount > CSV_LIMIT ? CSV_LIMIT : eventCount,
                        })}
                    />,
                    {
                        closeButton: (closeToastProps) => (
                            <CustomToastCloseButton closeToast={{ ...closeToastProps }} color={theme.iconFillColor} />
                        ),
                        className: layoutStyle.customToastSuccessMessage,
                    }
                );
            })
            .catch(() => {
                setIsExporting(false);
                if (!abortController.signal.aborted) {
                    setExportFailed(true);
                    if (filterState.eventTypes.length == 0 || filterState.productIds.length == 0) {
                        setFiltersMissing(true);
                    }
                }
            });
    };

    const [fetchCount, setFetchCount] = React.useState(0);

    function onFetchData() {
        setFetchCount(fetchCount + 1);
    }

    function onRefreshing() {
        setRefreshCount(refreshCount + 1);
    }

    const exportButton = (
        <div>
            {isExporting ? (
                <LoadingIndicator small={true} />
            ) : (
                <button
                    data-tip={t("LicenseEvent.export.tooltip")}
                    className={buttons.primaryButton}
                    onClick={() => {
                        usageStatisticsService.sendEvent({
                            category: Category.LICENSE_EVENTS,
                            action: Action.EXPORT_LICENSE_EVENTS,
                        });
                        formSearchQuery();
                    }}
                    data-testid={testIds.workArea.license.licenseEvents.exportButton}
                >
                    <ExportIcon color={theme.whiteColor} />
                    {t("LicenseEvent.export.title")}
                </button>
            )}
        </div>
    );

    return (
        <ApplicationLayout
            viewTitle={t("LicenseEvent.title")}
            view={
                <LicenseEventsTable
                    filterState={filterState}
                    setFilterState={setFilterState}
                    exportFailed={exportFailed}
                    setExportFailed={setExportFailed}
                    filtersMissing={filtersMissing}
                    setFiltersMissing={setFiltersMissing}
                    tableState={state}
                    setTableState={setTableState}
                    fetchCount={fetchCount}
                    refreshCount={refreshCount}
                    setRefreshCount={setRefreshCount}
                    exportLimitExceeded={exportLimitExceeded}
                    setExportLimitExceeded={setExportLimitExceeded}
                    isLargeExport={isLargeExport}
                    setIsLargeExport={setIsLargeExport}
                    setEventCount={setEventCount}
                    setStartExport={setStartExport}
                />
            }
            onHandleRefresh={onRefreshing}
            enableRefresh={true}
            onFetch={onFetchData}
            mainRoute={LICENSES_ROUTE}
            viewActions={state.licenseEvents.length > 0 ? exportButton : undefined}
        />
    );
}
